import { useState, useRef, useCallback } from "react";
import {
  Group,
  Text,
  Stack,
  ActionIcon,
  Button,
  FileButton,
  Box,
  Tooltip,
  Modal,
  Divider,
  Loader,
  Center,
} from "@mantine/core";
import {
  IconX,
  IconFileTypePdf,
  IconPhoto,
  IconCheck,
  IconAlertCircle,
  IconUpload,
  IconEye,
  IconDownload,
  IconFile,
  IconLoader2,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { accessioningService } from "../../services/workflows.service";
import { caseImagesService } from "../../services/caseImages.service";
import type { UploadedFile } from "../../types";
import { uploadAccessioningDocument } from "../../utils/accessioningUpload";

interface DocumentUploadProps {
  files: UploadedFile[];
  onFilesChange: (files: UploadedFile[]) => void;
  onUploadComplete?: () => void;
  maxFiles?: number;
  maxSize?: number; // in MB
  /** Custom presigned URL getter. Defaults to accessioningService.getPresignedUrl */
  getPresignedUrl?: (
    fileName: string,
    contentType: string
  ) => Promise<{ url: string; key: string }>;
  /** Content to render inside the drop zone, above the file list and buttons.
   *  This allows a parent to place saved document listings etc. inside the drag-and-drop area. */
  children?: React.ReactNode;
  /** When true, hides the inline file list (useful when the parent displays saved files separately).
   *  A success notification is shown instead after each upload. */
  hideFileList?: boolean;
  /** When true, hides the built-in Select/Scan buttons. */
  hideButtons?: boolean;
  /** Position for the action buttons. Defaults to 'bottom'. */
  actionsPosition?: "top" | "bottom";
  /** When true, shows a visible drop zone placeholder when empty. Defaults to false. */
  showDropZone?: boolean;
  /** Minimum height for the drop zone area. Defaults to undefined. */
  minHeight?: number | string;
  /** Placeholder text shown in the drop zone. Defaults to "Drag and drop files here". */
  dropZonePlaceholder?: string;
  /** When true, disables all interactions (add, remove, drop). */
  disabled?: boolean;
}

function truncateFileName(name: string, maxLen = 25): string {
  if (name.length <= maxLen) return name;
  const dotIdx = name.lastIndexOf(".");
  if (dotIdx <= 0) return `${name.slice(0, maxLen - 3)}...`;
  const ext = name.slice(dotIdx);
  const base = name.slice(0, dotIdx);
  const available = maxLen - ext.length - 3;
  if (available < 4) return `${name.slice(0, maxLen - 3)}...`;
  return `${base.slice(0, available)}...${ext}`;
}

function getFileName(uploadedFile: UploadedFile): string {
  return uploadedFile.fileName || uploadedFile.file?.name || "Unknown file";
}

function getFileType(uploadedFile: UploadedFile): string {
  return uploadedFile.file?.type || uploadedFile.mimeType || "";
}

export default function DocumentUpload({
  files,
  onFilesChange,
  onUploadComplete,
  maxFiles = 10,
  maxSize = 10,
  getPresignedUrl,
  children,
  hideFileList = false,
  hideButtons = false,
  actionsPosition = "bottom",
  showDropZone = false,
  minHeight,
  dropZonePlaceholder = "Drag and drop files here",
  disabled = false,
}: DocumentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const filesRef = useRef(files);
  filesRef.current = files;

  const acceptedTypes = [
    "image/jpeg",
    "image/png",
    "image/gif",
    "image/webp",
    "application/pdf",
  ];

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const uploadFileToS3 = async (file: File): Promise<UploadedFile> => {
    try {
      // Get presigned URL (use custom getter if provided)
      const presignedUrlFn =
        getPresignedUrl || accessioningService.getPresignedUrl;
      const key = await uploadAccessioningDocument(file, presignedUrlFn);

      return {
        file,
        s3Key: key,
        uploadProgress: 100,
      };
    } catch (error) {
      return {
        file,
        s3Key: "",
        uploadError: error instanceof Error ? error.message : "Upload failed",
      };
    }
  };

  const handleFilesSelect = async (newFiles: File[] | null) => {
    if (!newFiles || newFiles.length === 0) return;

    // Filter files by size and total count
    const maxSizeBytes = maxSize * 1024 * 1024;
    const validFiles = newFiles.filter((file) => {
      if (file.size > maxSizeBytes) {
        notifications.show({
          title: "File too large",
          message: `${file.name} exceeds ${maxSize}MB limit`,
          color: "red",
        });
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Get existing filenames for duplicate detection
    const existingFileNames = new Set(
      files.map((f) => (f.file?.name || f.fileName || "").toLowerCase())
    );

    // Filter out duplicates
    const uniqueFiles: File[] = [];
    const duplicateNames: string[] = [];

    validFiles.forEach((file) => {
      if (existingFileNames.has(file.name.toLowerCase())) {
        duplicateNames.push(file.name);
      } else {
        uniqueFiles.push(file);
        existingFileNames.add(file.name.toLowerCase()); // Prevent duplicates within the same batch
      }
    });

    // Show notification for duplicates
    if (duplicateNames.length > 0) {
      notifications.show({
        title: "Duplicate file(s) skipped",
        message: `${duplicateNames.join(", ")} already uploaded`,
        color: "yellow",
      });
    }

    // Continue with unique files only
    if (uniqueFiles.length === 0) return;

    // Check if we can add more files
    const remainingSlots = maxFiles - files.length;
    const filesToUpload = uniqueFiles.slice(0, remainingSlots);

    if (filesToUpload.length < uniqueFiles.length) {
      notifications.show({
        title: "File limit reached",
        message: `Only ${remainingSlots} more file(s) can be added`,
        color: "yellow",
      });
    }

    const pendingNames = new Set(
      filesToUpload.map((f) => f.name.toLowerCase())
    );

    // Add files with pending status
    const pendingFiles: UploadedFile[] = filesToUpload.map((file) => ({
      file,
      s3Key: "",
      uploadProgress: 0,
    }));

    onFilesChange([...files, ...pendingFiles]);

    // Upload files to S3
    const uploadResults = await Promise.all(
      filesToUpload.map((file) => uploadFileToS3(file))
    );

    const succeeded = uploadResults.filter((f) => f.s3Key);
    const failed = uploadResults.filter((f) => f.uploadError);

    if (failed.length > 0) {
      notifications.show({
        title: "Upload failed",
        message: `${failed.length} file(s) failed to upload`,
        color: "red",
      });
    }

    if (hideFileList && succeeded.length > 0) {
      notifications.show({
        title: "Document uploaded",
        message: `${succeeded.length} file(s) added successfully`,
        color: "green",
      });
    }

    // Use ref to get the latest files (accounts for removals during upload)
    const currentFiles = filesRef.current;
    const withoutPending = currentFiles.filter(
      (f) =>
        f.s3Key ||
        !pendingNames.has((f.file?.name || f.fileName || "").toLowerCase())
    );
    onFilesChange([...withoutPending, ...succeeded]);

    if (onUploadComplete && succeeded.length > 0) {
      onUploadComplete();
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || files.length >= maxFiles) return;

    const droppedFiles = Array.from(e.dataTransfer.files);

    // Filter to only accepted file types
    const validTypeFiles = droppedFiles.filter((file) => {
      const isValidType = acceptedTypes.includes(file.type);
      if (!isValidType) {
        notifications.show({
          title: "Invalid file type",
          message: `${file.name} is not a supported file type (PDF or image)`,
          color: "red",
        });
      }
      return isValidType;
    });

    if (validTypeFiles.length > 0) {
      handleFilesSelect(validTypeFiles);
    }
  };

  const handleRemove = (fileNameToRemove: string) => {
    const updatedFiles = files.filter(
      (f) => getFileName(f) !== fileNameToRemove
    );
    onFilesChange(updatedFiles);
  };

  const handleViewFile = useCallback(async (uploadedFile: UploadedFile) => {
    if (!uploadedFile.s3Key) return;
    setPreviewFile(uploadedFile);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const { url } = await caseImagesService.getDownloadUrl(
        uploadedFile.s3Key,
        getFileName(uploadedFile)
      );
      setPreviewUrl(url);
    } catch {
      notifications.show({
        title: "Cannot open file",
        message: "Failed to get download URL for the document",
        color: "red",
      });
      setPreviewFile(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewFile(null);
    setPreviewUrl(null);
  }, []);

  const handleOpenInNewTab = useCallback(async (uploadedFile: UploadedFile) => {
    if (!uploadedFile.s3Key) return;
    const newTab = window.open("", "_blank");
    try {
      const { url } = await caseImagesService.getDownloadUrl(
        uploadedFile.s3Key,
        getFileName(uploadedFile)
      );
      if (newTab) {
        newTab.location.href = url;
      }
    } catch {
      if (newTab) newTab.close();
      notifications.show({
        title: "Cannot open file",
        message: "Failed to get download URL for the document",
        color: "red",
      });
    }
  }, []);

  const canAddMore = files.length < maxFiles && !disabled;

  const getFileIcon = (uploadedFile: UploadedFile) => {
    const fileType = getFileType(uploadedFile);
    if (fileType === "application/pdf") {
      return <IconFileTypePdf size={20} color="var(--mantine-color-red-6)" />;
    }
    if (fileType.startsWith("image/")) {
      return <IconPhoto size={20} color="var(--mantine-color-blue-6)" />;
    }
    return <IconFile size={20} color="var(--mantine-color-gray-6)" />;
  };

  const getStatusText = (uploadedFile: UploadedFile) => {
    if (uploadedFile.uploadError)
      return { text: "Upload failed", color: "red" };
    if (uploadedFile.s3Key) return { text: "Uploaded", color: "green" };
    return { text: "Uploading...", color: "blue" };
  };

  const renderFileList = () => {
    if (hideFileList || files.length === 0) return null;
    return (
      <Stack gap={0}>
        {files.map((uploadedFile) => {
          const fileName = getFileName(uploadedFile);
          const isUploaded = !!uploadedFile.s3Key;
          const isUploading =
            !isUploaded &&
            !uploadedFile.uploadError &&
            uploadedFile.uploadProgress !== undefined;
          const statusInfo = getStatusText(uploadedFile);

          return (
            <Group
              key={uploadedFile.s3Key || fileName}
              gap="sm"
              wrap="nowrap"
              py={8}
              px={4}
              align="center"
              style={{
                borderBottom: "1px solid var(--mantine-color-gray-2)",
              }}
            >
              <Box style={{ flexShrink: 0 }}>{getFileIcon(uploadedFile)}</Box>

              <Box style={{ flex: 1, minWidth: 0 }}>
                <Tooltip
                  label={fileName}
                  disabled={fileName.length <= 25}
                  multiline
                  maw={300}
                >
                  <Text
                    size="xs"
                    fw={500}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {truncateFileName(fileName)}
                  </Text>
                </Tooltip>
                <Group gap={4} align="center">
                  {isUploading ? (
                    <IconLoader2
                      size={10}
                      color="var(--mantine-color-blue-5)"
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  ) : statusInfo.color === "green" ? (
                    <IconCheck size={10} color="var(--mantine-color-green-6)" />
                  ) : (
                    <IconAlertCircle
                      size={10}
                      color="var(--mantine-color-red-6)"
                    />
                  )}
                  <Text size="xs" c={statusInfo.color}>
                    {statusInfo.text}
                  </Text>
                </Group>
              </Box>

              <Group gap={4} style={{ flexShrink: 0 }}>
                {isUploaded && (
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={() => handleViewFile(uploadedFile)}
                    title="Preview"
                  >
                    <IconEye size={14} />
                  </ActionIcon>
                )}
                {isUploaded && (
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="gray"
                    onClick={() => handleOpenInNewTab(uploadedFile)}
                    title="Open in new tab"
                  >
                    <IconDownload size={14} />
                  </ActionIcon>
                )}
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={() => handleRemove(fileName)}
                  disabled={disabled}
                  title="Remove"
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
            </Group>
          );
        })}
      </Stack>
    );
  };

  const dropZoneNode = (
    <Box
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        position: "relative",
        borderRadius: "8px",
        border: isDragging
          ? "2px dashed var(--mantine-color-blue-5)"
          : showDropZone
          ? "2px dashed var(--mantine-color-gray-3)"
          : "2px dashed transparent",
        backgroundColor: isDragging
          ? "var(--mantine-color-blue-0)"
          : showDropZone
          ? "var(--mantine-color-gray-0)"
          : "transparent",
        transition: "all 0.15s ease",
        padding: "12px",
        minHeight: minHeight || (showDropZone ? 80 : undefined),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "default",
      }}
    >
      {isDragging ? (
        <>
          <IconUpload size={22} color="var(--mantine-color-blue-5)" />
          <Text size="sm" c="blue" fw={500} mt={4}>
            Drop files here
          </Text>
        </>
      ) : (
        <>
          <IconUpload size={20} color="var(--mantine-color-gray-4)" />
          <Text size="xs" c="dimmed" mt={4}>
            {dropZonePlaceholder}
          </Text>
          {!hideButtons && canAddMore && (
            <FileButton
              onChange={handleFilesSelect}
              accept="image/*,application/pdf"
              multiple
            >
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  mt={8}
                  leftSection={<IconUpload size={14} />}
                >
                  Select Files
                </Button>
              )}
            </FileButton>
          )}
        </>
      )}
    </Box>
  );

  return (
    <Stack gap="xs">
      {/* Action buttons at top position */}
      {actionsPosition === "top" && !hideButtons && canAddMore && (
        <Group justify="flex-end" gap="sm" align="center">
          <FileButton
            onChange={handleFilesSelect}
            accept="image/*,application/pdf"
            multiple
          >
            {(props) => (
              <Button
                {...props}
                size="xs"
                variant="light"
                leftSection={<IconUpload size={14} />}
              >
                {files.length === 0 ? "Select Files" : "Add More"}
              </Button>
            )}
          </FileButton>
        </Group>
      )}

      {children}

      {renderFileList()}

      {showDropZone && canAddMore && dropZoneNode}

      {files.length >= maxFiles && maxFiles > 1 && (
        <Text size="xs" c="dimmed" ta="center">
          Maximum {maxFiles} files reached
        </Text>
      )}

      <Modal.Root
        opened={previewFile !== null}
        onClose={handleClosePreview}
        size="90%"
        centered
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: "85vh",
          },
        }}
      >
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>
              {previewFile ? getFileName(previewFile) : "Preview"}
            </Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>

          <Modal.Body style={{ flex: 1, overflow: "auto", padding: 0 }}>
            {previewLoading || !previewUrl ? (
              <Center style={{ minHeight: 400 }}>
                <Loader />
              </Center>
            ) : previewFile && getFileType(previewFile).startsWith("image/") ? (
              <Center style={{ padding: 16 }}>
                <img
                  src={previewUrl}
                  alt={getFileName(previewFile)}
                  style={{ maxWidth: "100%", maxHeight: "70vh" }}
                />
              </Center>
            ) : (
              <iframe
                src={previewUrl}
                title={previewFile ? getFileName(previewFile) : "Preview"}
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "none",
                  display: "block",
                }}
              />
            )}
          </Modal.Body>

          <Divider />
          <Group justify="flex-end" p="md">
            <Button variant="default" onClick={handleClosePreview}>
              Close
            </Button>
          </Group>
        </Modal.Content>
      </Modal.Root>
    </Stack>
  );
}
